import React, {useContext, useRef, useState} from "react";
import FarmersSampleController from "../../controllers/FarmersSampleController";
import {dateFormat} from "../../date";
import PagedSearchTable, {PagedTableFunctions, TableColumn} from "../../components/PagedSearchTable";
import WarningPopup, {useWarningState} from "../../components/WarningPopup";
import TestFarmerSampleResponse from "../../controllers/TestFarmerSampleResponse";
import AppContext from "../../appContext";
import {wrapLoader} from "../../wrapper";
import FarmersSampleApiData from "../../controllers/FarmersSampleApiData";
import Dialog from "../../components/Dialog";
import Success from "../../components/Success";


function getValues(result: TestFarmerSampleResponse, name: string): string {
    return result.result?.find(r => r.name === name)?.lab_result ?? ''
}

const resultCol: | TableColumn<TestFarmerSampleResponse>[] = [
    {
        header: 'Lab',
        row: (item) => <span>{item.lab}</span>
    }, {
        header: 'Sample Number',
        row: (item) => <span>{item.sampleNumber}</span>
    }, {
        header: <>
            Client (Farm)
            <div className="text-xs whitespace-nowrap text-gray-500">
                Block
            </div>
        </>,
        key: "clientAndFarm",
        row: (item, index) =>
            <>
                {item.clientName} ({item.farmName})
                <div className="text-xs whitespace-nowrap text-gray-500">
                    {item.blockNumber}
                </div>
            </>
    },
    {
        header: '(Ca+Mg) / K',
        row: (item) => <span>{getValues(item, "(Ca+Mg) / K")}</span>
    },
    {
        header: 'Ca',
        row: (item) => <span>{getValues(item, "Ca")}</span>
    },
    {
        header: 'Ca:Mg',
        row: (item) => <span>{getValues(item, "Ca:Mg")}</span>
    },
    {
        header: 'Density',
        row: (item) => <span>{getValues(item, "Density")}</span>
    },
    {
        header: 'K',
        row: (item) => <span>{getValues(item, "K")}</span>
    },
    {
        header: 'Mg',
        row: (item) => <span>{getValues(item, "Mg")}</span>
    },
    {
        header: 'Mg:K',
        row: (item) => <span>{getValues(item, "Mg:K")}</span>
    },
    {
        header: 'Na',
        row: (item) => <span>{getValues(item, "Na")}</span>
    },
    {
        header: 'Na:K',
        row: (item) => <span>{getValues(item, "Na:K")}</span>
    },
    {
        header: 'P Bray',
        row: (item) => <span>{getValues(item, "P Bray")}</span>
    },
    {
        header: 'S Am AC',
        row: (item) => <span>{getValues(item, "S Am AC")}</span>
    },
    {
        header: 'pH',
        row: (item) => <span>{getValues(item, "pH")}</span>
    },
    {
        header: 'pHKCL',
        row: (item) => <span>{getValues(item, "pHKCL")}</span>
    },
    {
        header: 'K(B)',
        row: (item) => <span>{getValues(item, "K(B)")}</span>
    },
    {
        header: 'Ca(B)',
        row: (item) => <span>{getValues(item, "Ca(B)")}</span>
    },
    {
        header: 'Mg(B)',
        row: (item) => <span>{getValues(item, "Mg(B)")}</span>
    },
    {
        header: 'Na(B)',
        row: (item) => <span>{getValues(item, "Na(B)")}</span>
    },
    {
        header: 'Fe(B)',
        row: (item) => <span>{getValues(item, "Fe(B)")}</span>
    },
    {
        header: 'Mn(B)',
        row: (item) => <span>{getValues(item, "Mn(B)")}</span>
    },
    {
        header: 'Zn(B)',
        row: (item) => <span>{getValues(item, "Zn(B)")}</span>
    },
    {
        header: 'Cu(B)',
        row: (item) => <span>{getValues(item, "Cu(B)")}</span>
    },
    {
        header: 'C WB',
        row: (item) => <span>{getValues(item, "C WB")}</span>
    },
    {
        header: 'SOM',
        row: (item) => <span>{getValues(item, "SOM")}</span>
    },
    {
        header: 'Exch Acid KCL',
        row: (item) => <span>{getValues(item, "Exch Acid KCL")}</span>
    },
    {
        header: 'T-Value AmAc',
        row: (item) => <span>{getValues(item, "T-Value AmAc")}</span>
    },
    {
        header: 'Acid Sat AmAc',
        row: (item) => <span>{getValues(item, "Acid Sat AmAc")}</span>
    },
    {
        header: 'S-Value AmAC',
        row: (item) => <span>{getValues(item, "S-Value AmAC")}</span>
    },
]
const FarmersSamplesTab: React.FC<{
    archived: boolean,
    results: boolean,
    notCompleted: boolean
}> = (props) => {
    const context = useContext(AppContext);

    const pagedTableRef = useRef<PagedTableFunctions<TestFarmerSampleResponse>>()
    const resultTableRef = useRef<PagedTableFunctions<TestFarmerSampleResponse>>()

    const confirmationWarningState = useWarningState<number>(0)
    const restoreWarningState = useWarningState<number>(0)

    const [jsonData, setJsonData] = useState<FarmersSampleApiData>()
    const [showJson, setShowJson] = useState<boolean>(false)

    const [rowId, setRowId] = useState(-1)
    const [archiveState, setArchiveState] = useState(false)
    
    function archiveWarning(id: number, archive: boolean) {
        confirmationWarningState.show('Are you sure you want to archive this row?', id)
    }
    
    function callerComp() {
        if (props.notCompleted) {
            return FarmersSampleController.notCompleted
        }
        if (props.archived) {
            return FarmersSampleController.archived
        }
        return FarmersSampleController.completed
    }

    function getJson(id: number) {
        wrapLoader(context, FarmersSampleController.getApiData({id}), resp => {
            setShowJson(true)
            setJsonData(resp)
        })
    }

    function archive(id: number, state: boolean) {
        FarmersSampleController.archive({id, archive: state}).then(resp => {
            confirmationWarningState.hide();
            pagedTableRef.current?.refresh();
        }).then(() => {
            context.showSnack(<Success title={"Successfully"}/>)
        }).catch(() => {
            context.showSnack(<Success title={"Failed"}/>)
        })
    }

    function restore(id: number, state: boolean) {
        FarmersSampleController.archive({id, archive: state}).then(() => {
            restoreWarningState.hide()
            pagedTableRef.current?.refresh();
        }).then(() => {
            context.showSnack(<Success title={"Successfully"}/>)
        }).catch(() => {
            context.showSnack(<Success title={"Failed"}/>)
        })
    }

    function restoreWarning(id: number, archive: boolean) {
        setRowId(id)
        setArchiveState(archive)
        restoreWarningState.show("Are you sure you want to restore this row?", id)
    }

    const col: TableColumn<TestFarmerSampleResponse>[] = [
        {
            header: "lab",
            key: "lab",
            row: (item) => item.lab
        },
        {
            header: "sample",
            key: "sample",
            row: (item) => item.sampleNumber
        },
        {
            header: <>
                Client (Farm)
                <div className="text-xs whitespace-nowrap text-gray-500">
                    Block
                </div>
            </>,
            key: "clientAndFarm",
            row: (item, index) =>
                <>
                    {item.clientName} ({item.farmName})
                    <div className="text-xs whitespace-nowrap text-gray-500">
                        {item.blockNumber}
                    </div>
                </>
        },
        {
            header: <>
                Agent
                <div className="text-xs whitespace-nowrap text-gray-500">
                    Agronomist
                </div>
            </>,
            row: (item, index) => <>
                {item.agent}
                <div className="text-xs whitespace-nowrap text-gray-500">
                    {item.agronomist}
                </div>
            </>
        },
        {
            header: <>
                Date Synced
                <div className="text-xs whitespace-nowrap text-gray-500">
                    Date Captured
                </div>
            </>,
            row: (item, index) => <>
                {dateFormat(item.inserted)}
                <div className="text-xs text-gray-500">
                    {dateFormat(item.appAdded)}
                </div>
            </>
        },
    ]

    function extendedCol(): TableColumn<TestFarmerSampleResponse>[] {
        if (props.notCompleted) {
            return col.concat(
                {
                    header: <div className='text-right'>Actions</div>,
                    row: (item) => <div className='text-right'>
                        <button className="btn-primary btn-sm" onClick={_ => getJson(item.id)}>json</button>
                        <button className="btn-error btn-sm ml-1" onClick={_ => archiveWarning(item.id, true)}>x</button>
                    </div>
                })
        }

        return col.concat(
            {
                header: <div className='whitespace-nowrap'>Submitted Id</div>,
                row: (item) => item.submissionJobId
            },
            {
                header: <div className='whitespace-nowrap'>Submitted On</div>,
                row: (item) => dateFormat(item.submittedOn)
            },
            {
                header: 'Actions',
                row: (item) => <div className="text-right">
                    <button className="btn-primary btn-sm" onClick={_ => getJson(item.id)}>json</button>
                    {props.archived ?
                        <button className="btn-primary btn-sm ml-1" onClick={_ => restoreWarning(item.id, false)}>restore
                        </button> : null}
                </div>
            }
        )
    }
    
    return <>

        {props.results
            ? <PagedSearchTable componentRef={resultTableRef} 
                                call={FarmersSampleController.getFarmerSampleResults}
                                downloadExcelCall={FarmersSampleController.pagedExcel}
                                columns={resultCol} keyExtractor={i => i.id}/>
            : 
            <PagedSearchTable componentRef={pagedTableRef}
                                call={callerComp()}
                                columns={extendedCol()}
                                keyExtractor={i => i.id}/>
        }
        
        
        <WarningPopup state={confirmationWarningState} onYes={() => archive(confirmationWarningState.state.data, true)}/>
        <WarningPopup state={restoreWarningState} onYes={() => restore(restoreWarningState.state.data, false)}/>

        <Dialog show={showJson} setShow={setShowJson} title="Json" body={<>
            <div className="flex text-left">
                <div className="w-1/2 px-2">
                    <p className="text-xl font-bold">Original</p>
                    <pre className="whitespace-pre-wrap break-words text-xs">{JSON.stringify(jsonData?.original)}</pre>
                </div>
                <div className="w-1/2 px-2">
                    <p className="text-xl font-bold">Mapped</p>
                    <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(jsonData?.mapped)}</pre>
                </div>
            </div>
            <div className="px-2">
                <p className="text-xl font-bold">Test Farmers Sample Data</p>
                <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(jsonData?.testFarmersSampleData)}</pre>
            </div>
        </>}/>
        
    </>
} 

export default FarmersSamplesTab;